<script setup lang="ts">
import { ref, computed, watch, onBeforeUnmount, nextTick } from "vue";
import { useI18n } from "vue-i18n";
import TinyUserThumb from "@/components/TinyUserThumb.vue";
import Spinner from "@/components/Spinner.vue";
import ConformationModal from "@/components/Modals/ConformationModal.vue";
import InformationText from "@/components/InformationText.vue";
import ShortCommentsBottomSheet from "./ShortCommentsBottomSheet.vue";
import { useShortsStore } from "@/stores/modules/shorts/shorts";
import { useGroupsStore } from "@/stores/modules/groups/groups";
import { DEFAULT_SHORT_MAX_DURATION_SECONDS } from "@/constants/shorts";
import { useAuthStore } from "@/stores/modules/auth/auth";
import { firebaseStorage } from "@/helpers/firebase/firebase";
import { ref as storageRef, getDownloadURL } from "firebase/storage";
import { clearCachedImages } from "@/helpers/cache/image-cache";
import { CacheTypes } from "@/enums/cache-types";
import { dateTimeAgo } from "@/helpers/dates/date-time-ago";
import {
  getBlurhashUrlSync,
  requestBlurhashUrl,
} from "@/helpers/blurhash/blurhash-url-queue";
import { formatMemberNamesList } from "@/helpers/utils/format-member-names";
import {
  setSuccessToast,
  setWarningToast,
  setErrorToast,
} from "@/helpers/notifications/toast";
import DefaultButton from "../Buttons/DefaultButton.vue";

const { t } = useI18n();
const shortsStore = useShortsStore();
const groupsStore = useGroupsStore();
const authStore = useAuthStore();

const videoElement = ref<HTMLVideoElement | null>(null);
const videoUrl = ref<string>("");
const isMuted = ref(true);
const isRepeatMode = ref(false);
const isPaused = ref(false);
const isBuffering = ref(false);
const isVideoPlaying = ref(false);
const isDownloadingVideo = ref(false);
const placeholderHashUrl = ref<string>("");
const placeholderThumbnailUrl = ref<string>("");
const progressPercent = ref(0);
const commentsOpen = ref(false);
const wasPlayingBeforeComments = ref(false);
const showDeleteModal = ref(false);
const isDeleting = ref(false);
const showReportModal = ref(false);
const isReporting = ref(false);
const isCaptionExpanded = ref(false);

const isCaptionLong = computed(() => {
  const cap = currentShort.value?.caption;
  if (!cap) return false;
  return cap.length > 35 || cap.includes("\n");
});

function toggleCaption() {
  if (isCaptionLong.value) {
    isCaptionExpanded.value = !isCaptionExpanded.value;
  }
}

const groupId = computed(() => groupsStore.groupData?._id || "");
const groupShorts = computed(() => shortsStore.shorts);

const currentShort = computed(() => shortsStore.activeShort);

const currentIndex = computed(() => {
  if (!currentShort.value) return -1;
  return groupShorts.value.findIndex((s) => s._id === currentShort.value?._id);
});

const authorName = computed(() => {
  if (!currentShort.value) return "";
  const member =
    groupsStore.groupData?.members?.[currentShort.value.created_by];
  return member?.name || t("global.unknown_user");
});

const timeAgo = computed(() => {
  if (!currentShort.value) return "";
  const date = currentShort.value.created_at || currentShort.value.timestamp;
  return dateTimeAgo(new Date(date));
});

const canDelete = computed(() => {
  if (!currentShort.value) return false;
  const isCreator = currentShort.value.created_by === authStore.authUuid;
  const isAdmin = groupsStore.isAdmin;
  return isCreator || isAdmin;
});

const isReported = computed(() => {
  return (currentShort.value?.reported_by?.length ?? 0) > 0;
});

const isRedacted = computed(() => {
  return Boolean(currentShort.value?.redacted);
});

const isReportedByMe = computed(() => {
  return Boolean(
    authStore.authUuid &&
    currentShort.value?.reported_by?.includes(authStore.authUuid),
  );
});

const reportedNames = computed(() => {
  if (!groupsStore.groupData?.members || !currentShort.value?.reported_by) {
    return "";
  }
  return formatMemberNamesList(
    currentShort.value.reported_by,
    groupsStore.groupData.members,
    t("global.deleted_user"),
  );
});

let downloadTimeout: ReturnType<typeof setTimeout> | null = null;
// Short whose HTTPS fallback was already tried; prevents an error -> reload loop when the fallback also fails
let httpsFallbackShortId: string | null = null;

// Resolve URL and play when another short opens or its moderation state
// changes. Not on every snapshot: a comment count update must not restart it.
watch(
  [() => currentShort.value?._id, isReported, isRedacted],
  async () => {
    const short = currentShort.value;
    if (downloadTimeout) {
      clearTimeout(downloadTimeout);
      downloadTimeout = null;
    }

    if (videoElement.value) {
      videoElement.value.pause();
      videoElement.value.removeAttribute("src");
      videoElement.value.load();
    }

    videoUrl.value = "";
    progressPercent.value = 0;
    isVideoPlaying.value = false;
    isBuffering.value = false;
    isCaptionExpanded.value = false;
    httpsFallbackShortId = null;

    if (!short || (short.reported_by?.length ?? 0) > 0) {
      placeholderThumbnailUrl.value = "";
      placeholderHashUrl.value = "";
      isDownloadingVideo.value = false;
      return;
    }

    if (short.redacted) {
      placeholderThumbnailUrl.value = "";
      placeholderHashUrl.value = "";
      isDownloadingVideo.value = false;
      return;
    }

    // 1. Immediately provide cached thumbnail or decoded blurhash (zero blank frames)
    const cachedThumb = shortsStore.thumbnailsById[short._id]?.url || "";
    placeholderThumbnailUrl.value = cachedThumb;

    placeholderHashUrl.value = short.blurhash
      ? getBlurhashUrlSync(short.blurhash) || ""
      : "";

    if (!placeholderHashUrl.value && short.blurhash) {
      requestBlurhashUrl(short.blurhash, (url) => {
        if (currentShort.value?._id === short._id) {
          placeholderHashUrl.value = url;
        }
      });
    }

    // 2. Resolve thumbnail in parallel if not already cached
    if (!cachedThumb) {
      shortsStore.resolveThumbnailUrl(short).then((thumbUrl) => {
        if (thumbUrl && currentShort.value?._id === short._id) {
          placeholderThumbnailUrl.value = thumbUrl;
        }
      });
    }

    // 3. Delay showing "Loading video" badge by 150ms to prevent visual flicker on prefetched videos
    const cachedVideo = shortsStore.videosById[short._id]?.url;
    if (!cachedVideo) {
      downloadTimeout = setTimeout(() => {
        if (currentShort.value?._id === short._id && !videoUrl.value) {
          isDownloadingVideo.value = true;
        }
      }, 150);
    } else {
      isDownloadingVideo.value = false;
    }

    // 4. Download / resolve the video as Blob
    try {
      const url = await shortsStore.resolveVideoUrl(short);
      if (downloadTimeout) {
        clearTimeout(downloadTimeout);
        downloadTimeout = null;
      }
      if (currentShort.value?._id === short._id) {
        videoUrl.value = url;
        isDownloadingVideo.value = false;
        await nextTick();
        if (
          url &&
          videoElement.value &&
          currentShort.value?._id === short._id
        ) {
          videoElement.value.currentTime = 0;
          videoElement.value.load();
          if (commentsOpen.value) {
            isPaused.value = true;
          } else {
            videoElement.value
              .play()
              .then(() => {
                isPaused.value = false;
              })
              .catch((playErr) => {
                console.warn(
                  "Video play was interrupted or rejected:",
                  playErr,
                );
                isPaused.value = true;
              });
          }
        }
      }
    } catch (err) {
      if (downloadTimeout) {
        clearTimeout(downloadTimeout);
        downloadTimeout = null;
      }
      console.error("Failed to load video in viewer:", err);
      if (currentShort.value?._id === short._id) {
        isDownloadingVideo.value = false;
      }
    }
  },
  { immediate: true },
);

async function onVideoError(e: Event) {
  if (!currentShort.value) return;
  const brokenShort = currentShort.value;
  if (httpsFallbackShortId === brokenShort._id) {
    console.error(
      "Short video failed to play via HTTPS fallback, giving up:",
      brokenShort._id,
      e,
    );
    isBuffering.value = false;
    isDownloadingVideo.value = false;
    isPaused.value = true;
    return;
  }
  httpsFallbackShortId = brokenShort._id;
  console.warn(
    "Short video failed to play via blob URL, falling back to direct HTTPS:",
    brokenShort._id,
    e,
  );

  // Evict broken blob from memory and IndexedDB cache
  shortsStore.evictVideo(brokenShort._id);
  await clearCachedImages(`${CacheTypes.ShortVideo}:${brokenShort._id}`);

  // Fallback directly to Firebase Storage HTTPS download URL
  try {
    const reference = storageRef(
      firebaseStorage,
      brokenShort.absolute_video_url,
    );
    const httpsUrl = await getDownloadURL(reference);
    if (currentShort.value?._id === brokenShort._id) {
      videoUrl.value = httpsUrl;
      await nextTick();
      if (videoElement.value) {
        videoElement.value.load();
        if (!commentsOpen.value) {
          videoElement.value.play().catch(() => {
            isPaused.value = true;
          });
        }
      }
    }
  } catch (err) {
    console.error("Direct HTTPS download URL fallback failed:", err);
  }
}

// When comments open/close, pause/resume video
watch(
  () => commentsOpen.value,
  (open) => {
    if (open) {
      if (videoElement.value && !videoElement.value.paused) {
        wasPlayingBeforeComments.value = true;
        videoElement.value.pause();
      } else {
        wasPlayingBeforeComments.value = false;
      }
    } else {
      if (wasPlayingBeforeComments.value) {
        wasPlayingBeforeComments.value = false;
        videoElement.value?.play().catch(() => {});
      }
    }
  },
);

onBeforeUnmount(() => {
  if (downloadTimeout) {
    clearTimeout(downloadTimeout);
    downloadTimeout = null;
  }
  if (videoElement.value) {
    videoElement.value.pause();
    videoElement.value.removeAttribute("src");
    videoElement.value.load();
  }
  shortsStore.pruneVideoMemory([]);
});

function onPlaying() {
  isVideoPlaying.value = true;
  isBuffering.value = false;
  isPaused.value = false;
  // Trigger prefetch for next short
  if (currentIndex.value < groupShorts.value.length - 1) {
    const next = groupShorts.value[currentIndex.value + 1];
    if (next && (next.reported_by?.length ?? 0) === 0) {
      shortsStore.prefetchVideo(next);
    }
  }
}

function onCanPlay() {
  if (commentsOpen.value) return;
  if (videoElement.value && !isPaused.value && !isVideoPlaying.value) {
    videoElement.value.play().catch(() => {});
  }
}

function onTimeUpdate() {
  if (!videoElement.value) return;
  const current = videoElement.value.currentTime;
  const duration =
    videoElement.value.duration ||
    currentShort.value?.duration ||
    DEFAULT_SHORT_MAX_DURATION_SECONDS;
  progressPercent.value = Math.min((current / duration) * 100, 100);
}

function onVideoEnded() {
  if (isRepeatMode.value) {
    // Repeat current short
    if (videoElement.value) {
      videoElement.value.currentTime = 0;
      videoElement.value.play().catch(() => {});
    }
  } else if (currentIndex.value < groupShorts.value.length - 1) {
    // Advance to next short
    goToNextShort();
  } else {
    // Last short in list — loop back to current
    if (videoElement.value) {
      videoElement.value.currentTime = 0;
      videoElement.value.play().catch(() => {});
    }
  }
}

function goToNextShort() {
  if (currentIndex.value < groupShorts.value.length - 1) {
    const next = groupShorts.value[currentIndex.value + 1];
    if (next) {
      shortsStore.openViewer(next);
    }
  }
}

function goToPreviousShort() {
  if (currentIndex.value > 0) {
    const prev = groupShorts.value[currentIndex.value - 1];
    if (prev) {
      shortsStore.openViewer(prev);
    }
  }
}

function onViewerClick(e: MouseEvent) {
  if (commentsOpen.value) return;
  const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
  const x = e.clientX - rect.left;
  const width = rect.width;

  // Left 25% tap goes back
  if (x < width * 0.25) {
    goToPreviousShort();
  }
  // Right 25% tap advances
  else if (x > width * 0.75) {
    goToNextShort();
  }
  // Middle tap toggles play/pause
  else {
    togglePlayPause();
  }
}

function togglePlayPause() {
  if (isReported.value) return;
  if (commentsOpen.value) return;
  if (!videoElement.value) return;
  if (videoElement.value.paused) {
    videoElement.value.play().catch(() => {});
    isPaused.value = false;
  } else {
    videoElement.value.pause();
    isPaused.value = true;
  }
}

function toggleMute() {
  isMuted.value = !isMuted.value;
  if (videoElement.value) {
    videoElement.value.muted = isMuted.value;
  }
}

function toggleRepeatMode() {
  isRepeatMode.value = !isRepeatMode.value;
}

function openComments() {
  commentsOpen.value = true;
}

function closeViewer() {
  if (downloadTimeout) {
    clearTimeout(downloadTimeout);
    downloadTimeout = null;
  }
  if (videoElement.value) {
    videoElement.value.pause();
    videoElement.value.removeAttribute("src");
    videoElement.value.load();
  }
  isVideoPlaying.value = false;
  commentsOpen.value = false;
  showDeleteModal.value = false;
  showReportModal.value = false;
  wasPlayingBeforeComments.value = false;
  videoUrl.value = "";
  shortsStore.closeViewer();
  shortsStore.pruneVideoMemory([]);
}

function confirmDelete() {
  showDeleteModal.value = true;
}

async function executeDelete() {
  if (!currentShort.value || !groupId.value) return;
  isDeleting.value = true;
  try {
    await shortsStore.deleteShort(groupId.value, currentShort.value);
    showDeleteModal.value = false;
    setSuccessToast({
      message: t("pages.shorts.deleted_success"),
    });
  } catch (err) {
    console.error("Failed to delete short:", err);
  } finally {
    isDeleting.value = false;
  }
}

function confirmReport() {
  if (isReportedByMe.value) {
    setWarningToast({
      message: t("components.report.toastReported"),
    });
    return;
  }
  showReportModal.value = true;
}

async function executeReport() {
  if (!currentShort.value || !groupId.value || !authStore.authUuid) {
    setErrorToast({
      message: t("components.report.toastNotReported"),
    });
    return;
  }

  isReporting.value = true;
  const shortId = currentShort.value._id;
  try {
    await shortsStore.reportShort(groupId.value, shortId, authStore.authUuid);
    showReportModal.value = false;
    setSuccessToast({
      message: t("pages.shorts.reported_success"),
    });
    closeViewer();
  } catch (err) {
    console.error("Failed to report short:", err);
    setErrorToast({
      message: t("components.report.toastNotReported"),
    });
  } finally {
    isReporting.value = false;
  }
}
</script>

<template>
  <Teleport to="body">
    <div
      v-if="shortsStore.viewerOpen && currentShort"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black-dark/50 px-3 py-3 backdrop-blur-md select-none sm:px-4 sm:py-6"
      @click.self="closeViewer"
    >
      <!-- Main Player Viewport (9:16 vertical card) -->
      <div
        class="relative flex h-full max-h-[96vh] w-full max-w-md flex-col justify-between overflow-hidden rounded-2xl border-2 border-black-dark bg-black-dark shadow-2xl sm:max-h-[92vh] sm:rounded-3xl"
      >
        <!-- Top Segmented Progress Bars -->
        <div class="absolute top-3 right-0 left-0 z-30 flex gap-1.5 px-3">
          <div
            v-for="(s, index) in groupShorts"
            :key="s._id"
            class="h-1 flex-1 overflow-hidden rounded-full bg-white/30"
          >
            <div
              class="h-full bg-kekkle-yellow transition-all duration-100 ease-linear"
              :style="{
                width:
                  index < currentIndex
                    ? '100%'
                    : index === currentIndex && !isReported && !isRedacted
                      ? `${progressPercent}%`
                      : '0%',
              }"
            />
          </div>
        </div>

        <!-- Top Header Navigation & Creator Info -->
        <div
          class="absolute top-0 right-0 left-0 z-30 flex items-center justify-between px-3 pt-5 pb-3"
        >
          <div
            class="flex items-center gap-2 rounded-full border border-white/10 bg-black-dark/40 px-2 py-1 backdrop-blur-xs"
          >
            <tiny-user-thumb :uuid="currentShort.created_by" />
            <div class="pr-1 text-left leading-tight">
              <p class="text-sm font-bold text-white drop-shadow-sm">
                {{ authorName }}
              </p>
              <p class="text-[11px] text-gray-300 drop-shadow-sm">
                {{ timeAgo }}
              </p>
            </div>
          </div>

          <button
            class="flex h-9 w-9 items-center justify-center rounded-xl border-2 border-black-dark bg-white text-sm font-bold text-black-dark shadow-dark-hard-sm transition-all active:translate-x-px active:translate-y-px active:shadow-none"
            @click="closeViewer"
          >
            ✕
          </button>
        </div>

        <!-- Reported State (when short is reported) -->
        <div
          v-if="isReported"
          class="relative flex flex-1 flex-col items-center justify-center bg-black-dark p-6 text-center select-none"
          style="background-color: #040023"
          @click="onViewerClick"
        >
          <div class="w-full max-w-xs">
            <InformationText color="yellow" solid>
              <div class="text-left">
                <p class="mb-1 text-base font-bold text-black-dark">
                  {{ $t("components.report.reportTitle") }}
                </p>
                <p class="text-xs leading-relaxed font-medium text-black-dark">
                  {{ $t("components.report.hasBeenReportedBy") }}
                  <span class="font-bold">{{
                    reportedNames || $t("global.member")
                  }}</span
                  >.
                  {{ $t("components.report.adminsWillDetermine") }}
                </p>
              </div>
            </InformationText>
          </div>
        </div>

        <!-- Redacted State (when short was removed by content moderation) -->
        <div
          v-else-if="isRedacted"
          class="relative flex flex-1 flex-col items-center justify-center bg-black-dark p-6 text-center select-none"
          style="background-color: #040023"
          @click="onViewerClick"
        >
          <div class="relative z-10 w-full max-w-xs">
            <InformationText color="yellow" solid>
              <div class="text-left">
                <p class="mb-1 text-base font-bold text-black-dark">
                  {{ $t("components.report.reportTitle") }}
                </p>
                <p class="text-xs leading-relaxed font-medium text-black-dark">
                  {{
                    currentShort.caption ||
                    "This short was removed by content moderation."
                  }}
                </p>
              </div>
            </InformationText>
          </div>
        </div>

        <!-- Video Player Element & Buffering Spinner (when NOT reported and NOT redacted) -->
        <div
          v-else
          class="relative flex flex-1 items-center justify-center overflow-hidden bg-black-dark"
          style="background-color: #040023"
          @click="onViewerClick"
        >
          <!-- Placeholder Layers (visible until video starts playing) -->
          <div
            v-if="!isVideoPlaying"
            class="pointer-events-none absolute inset-0 z-10 flex items-center justify-center overflow-hidden bg-black-dark"
            style="background-color: #040023"
          >
            <!-- Blurhash preview -->
            <img
              v-if="placeholderHashUrl"
              :src="placeholderHashUrl"
              alt="Short preview"
              class="h-full w-full object-contain"
            />

            <!-- Thumbnail poster (if available) -->
            <img
              v-if="placeholderThumbnailUrl"
              :src="placeholderThumbnailUrl"
              alt="Short thumbnail"
              class="absolute inset-0 h-full w-full object-contain"
            />

            <!-- Download loading overlay -->
            <div
              v-if="isDownloadingVideo"
              class="absolute inset-0 flex flex-col items-center justify-center gap-3 bg-black-dark/50"
            >
              <span
                class="rounded-xl border-2 border-black-dark bg-white px-3 py-1 text-xs font-bold text-black-dark shadow-dark-hard-sm"
              >
                {{ $t("pages.shorts.loading_video") }}
              </span>
            </div>
          </div>

          <video
            ref="videoElement"
            class="h-full w-full bg-black-dark object-contain"
            style="background-color: #040023"
            playsinline
            autoplay
            :poster="placeholderThumbnailUrl || placeholderHashUrl || undefined"
            :muted="isMuted"
            :src="videoUrl"
            @timeupdate="onTimeUpdate"
            @ended="onVideoEnded"
            @waiting="isBuffering = true"
            @playing="onPlaying"
            @canplay="onCanPlay"
            @pause="isPaused = true"
            @error="onVideoError"
          />

          <!-- Play/Pause & Buffering Indicators (while video is active) -->
          <div
            v-if="isVideoPlaying && isBuffering"
            class="pointer-events-none absolute inset-0 z-20 flex items-center justify-center bg-black-dark/20"
          >
            <spinner class="h-8 w-8" />
          </div>
          <div
            v-else-if="isVideoPlaying && isPaused"
            class="pointer-events-none absolute inset-0 z-20 flex items-center justify-center"
          >
            <DefaultButton color="yellow">▶</DefaultButton>
          </div>
        </div>

        <!-- Bottom Overlay: Caption and Floating Action Buttons -->
        <div
          class="pointer-events-none absolute right-0 bottom-0 left-0 z-30 flex items-end justify-between gap-3 p-4 pb-6"
        >
          <!-- Caption (isolated on bottom-left, collapsible) -->
          <div
            v-if="!isReported && !isRedacted && currentShort.caption"
            class="pointer-events-auto flex-1 rounded-xl border border-white/15 bg-black-dark/60 p-2.5 text-left backdrop-blur-xs transition-all"
            :class="[
              isCaptionLong ? 'cursor-pointer' : '',
              isCaptionExpanded
                ? 'scrollbar-hide max-h-[35vh] overflow-y-auto'
                : 'overflow-hidden',
            ]"
            @click.stop="toggleCaption"
          >
            <div
              v-if="!isCaptionExpanded"
              class="flex items-center justify-between gap-2"
            >
              <p
                class="line-clamp-1 flex-1 text-sm font-medium break-all text-white drop-shadow-xs"
              >
                {{ currentShort.caption }}
              </p>
              <button
                v-if="isCaptionLong"
                type="button"
                class="shrink-0 text-xs font-bold text-kekkle-yellow hover:underline"
                @click.stop="toggleCaption"
              >
                {{ $t("pages.shorts.show_more") }}
              </button>
            </div>
            <div v-else class="flex flex-col gap-1.5">
              <p
                class="text-sm font-medium wrap-break-word whitespace-pre-line text-white drop-shadow-xs"
              >
                {{ currentShort.caption }}
              </p>
              <div class="flex justify-end">
                <button
                  type="button"
                  class="text-xs font-bold text-kekkle-yellow hover:underline"
                  @click.stop="toggleCaption"
                >
                  {{ $t("pages.shorts.show_less") }}
                </button>
              </div>
            </div>
          </div>
          <div v-else class="flex-1" />

          <!-- Vertical Action Sidebar -->
          <div class="pointer-events-auto flex flex-col items-center gap-3">
            <!-- Repeat / Next Toggle Button -->
            <button
              v-if="!isReported && !isRedacted"
              type="button"
              class="flex h-11 w-11 items-center justify-center rounded-xl border-2 border-black-dark bg-white text-lg text-black-dark shadow-dark-hard-sm transition-all active:translate-x-px active:translate-y-px active:shadow-none"
              @click.stop="toggleRepeatMode"
            >
              {{ isRepeatMode ? "🔁" : "⏭️" }}
            </button>

            <!-- Mute / Unmute Button -->
            <button
              v-if="!isReported && !isRedacted"
              type="button"
              class="flex h-11 w-11 items-center justify-center rounded-xl border-2 border-black-dark bg-white text-lg text-black-dark shadow-dark-hard-sm transition-all active:translate-x-px active:translate-y-px active:shadow-none"
              @click.stop="toggleMute"
            >
              {{ isMuted ? "🔇" : "🔊" }}
            </button>

            <!-- Comments Button -->
            <button
              type="button"
              class="flex min-w-[44px] flex-col items-center justify-center rounded-xl border-2 border-black-dark bg-white px-2 py-1.5 shadow-dark-hard-sm transition-all active:translate-x-px active:translate-y-px active:shadow-none"
              @click.stop="openComments"
            >
              <span class="text-base">💬</span>
              <span class="text-[11px] font-bold text-black-dark">
                {{ currentShort.number_of_comments || 0 }}
              </span>
            </button>

            <!-- Delete Button (Creator or Admin) -->
            <button
              v-if="canDelete"
              type="button"
              class="flex h-11 w-11 items-center justify-center rounded-xl border-2 border-black-dark bg-kekkle-red text-sm text-white shadow-dark-hard-sm transition-all active:translate-x-px active:translate-y-px active:shadow-none"
              @click.stop="confirmDelete"
            >
              🗑️
            </button>

            <!-- Report Button -->
            <button
              v-else-if="!isRedacted"
              type="button"
              class="flex h-11 w-11 items-center justify-center rounded-xl border-2 border-black-dark bg-white text-xs text-black-dark shadow-dark-hard-sm transition-all active:translate-x-px active:translate-y-px active:shadow-none"
              :class="
                isReported || isReportedByMe
                  ? 'cursor-not-allowed opacity-50'
                  : ''
              "
              :disabled="isReported || isReportedByMe"
              @click.stop="confirmReport"
            >
              🚩
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Short Comments Bottom Sheet -->
    <ShortCommentsBottomSheet
      v-if="currentShort && commentsOpen"
      v-model:open="commentsOpen"
      :group-id="groupId"
      :short-id="currentShort._id"
    />

    <!-- Confirm Delete Modal -->
    <ConformationModal
      :visible="showDeleteModal"
      :danger="true"
      :loading="isDeleting"
      :confirm-text="$t('global.inputs.labels.delete')"
      :cancel-text="$t('global.cancel')"
      @cancel="showDeleteModal = false"
      @confirm="executeDelete"
    >
      <div class="mb-4 text-left">
        <h3 class="text-lg font-bold text-gray-900 dark:text-white">
          {{ $t("pages.shorts.confirm_delete_title") }}
        </h3>
        <p class="mt-2 text-sm text-gray-600 dark:text-gray-300">
          {{ $t("pages.shorts.confirm_delete") }}
        </p>
      </div>
    </ConformationModal>

    <!-- Confirm Report Modal -->
    <ConformationModal
      :visible="showReportModal"
      :danger="true"
      :loading="isReporting"
      :confirm-text="$t('components.report.report')"
      :cancel-text="$t('components.report.close')"
      @cancel="showReportModal = false"
      @confirm="executeReport"
    >
      <div class="mb-4 text-left">
        <h3 class="text-lg font-bold text-gray-900 dark:text-white">
          {{ $t("components.report.reportTitle") }}
        </h3>
        <p class="mt-2 text-sm text-gray-600 dark:text-gray-300">
          {{ $t("components.report.reportText") }}
        </p>
        <InformationText
          class="mt-4"
          color="yellow"
          :information="$t('components.report.reportInformation')"
        />
      </div>
    </ConformationModal>
  </Teleport>
</template>
